import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Card,
  CardContent,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Radio,
  Select,
  MenuItem,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/hook";
import {
  getUserPermissionAsync,
  isUserPermissionDataLoading,
  userPermissionDataFromApi,
} from "../../redux/getUserPermission/reducer";
import { useNavigate } from "react-router-dom";
import { msalInstance } from "../../index";
import { authScopes } from "../../authConfig";
import { AccountInfo } from "@azure/msal-common";
import "../../styles/css/spinner.css";

import { dataFromApi } from "../../redux/getApiData/reducer";
import { submitUserPermissionDataAsync } from "../../redux/updateUserPermission/reducer";
import { useTranslation } from "react-i18next";
import brand from "../../brand";

export const AdminUserPermissionPage: React.FC = () => {
  const CUSTOM_ROLES = ["custom1", "custom2", "custom3"];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const apiData = useAppSelector(dataFromApi);
  const isLoading = useAppSelector(isUserPermissionDataLoading);
  const userPermissionData = useAppSelector(userPermissionDataFromApi);

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);

  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isUserRoleChange, setIsUserRoleChange] = useState(false);
  const [isRolePermissionChange, setIsRolePermissionChange] = useState(false);
  const [roleChangeData, setRoleChangeData] = useState([]);
  function setUserPermissionData(
    source,
    isUserRoleChange = true,
    isRolePermissionChange = true
  ) {
    const rawRoleData = source.roles
      .map((roleData) => roleData.role_name)
      .filter((item) => !CUSTOM_ROLES.includes(item));
    const customRoleEntries = CUSTOM_ROLES.filter(
      (role) => !rawRoleData.includes(role)
    );
    const roleData = [...rawRoleData, ...customRoleEntries];
    setRoles(roleData);
    const userUpn = source.users.map((userData) => userData.upn);
    const defaultUsers = userUpn.filter(
      (userData) => userData === "default_user"
    );
    const usersList = userUpn.filter((userData) => userData !== "default_user");
    const newUsers = apiData.tenant.upn.filter(
      (user) => usersList.indexOf(user) == -1
    );
    const allUsers = [
      ...defaultUsers.map((u) => ({ email: u, type: "default" })),
      ...usersList.map((u) => ({ email: u, type: "user" })),
      ...newUsers.map((u) => ({ email: u, type: "new" })),
    ];
    setUsers(allUsers);
    if (isUserRoleChange) {
      setUserRoles(source.users);
    }
    if (isRolePermissionChange) {
      const customRoleEntries = CUSTOM_ROLES.filter(
        (role) => !rawRoleData.includes(role)
      ).map((role) => ({
        role_name: role,
        permission: {
          report_email: false,
          report_identity: false,
          admin_collector: false,
          admin_system_credential: false,
          ticket_manage_sharedmailbox: false,
          ticket_new_siteuser: false,
          ticket_offboard: false,
          report_license: false,
          ticket_onboard: false,
          ticket_manage_site: false,
          admin_user_permission: false,
          report_security: false,
          ticket_others: false,
        },
      }));
      setRolePermissions([...source.roles, ...customRoleEntries]);
    }
  }
  function getData(isUserRoleChange = true, isRolePermissionChange = true) {
    dispatch(getUserPermissionAsync()).then((requestStatus) => {
      if (requestStatus?.meta.requestStatus == "fulfilled") {
        setUserPermissionData(
          requestStatus.payload,
          isUserRoleChange,
          isRolePermissionChange
        );
      } else if (requestStatus?.meta.requestStatus == "rejected") {
        const error = JSON.parse(requestStatus.payload?.message);
        console.log(error);
        if (error.message === "InteractionRequiredAuthError") {
          msalInstance.acquireTokenRedirect({
            ...authScopes,
            account: msalInstance.getActiveAccount() as AccountInfo,
          });
        } else {
          if (error.status === 401) {
            navigate("/error", {
              state: { message: "userPermissionUnauthorized" },
            });
          }
          if (error.status === 500) {
            navigate("/error", {
              state: { message: "internalServerError" },
            });
          }
        }
      }
    });
  }
  useEffect(() => {
    if (apiData) {
      if (!userPermissionData) {
        getData();
      } else {
        setUserPermissionData(userPermissionData);
      }
    }
  }, [msalInstance]);
  const discardUserRoleChanges = () => {
    setIsUserRoleChange(false);
    setUserPermissionData(userPermissionData, true, false);
  };

  const discardRolePermissionChanges = () => {
    setIsRolePermissionChange(false);
    setUserPermissionData(userPermissionData, false, true);
  };
  const toggleRole = (role: string) => {
    if (!selectedUser) return;
    setIsUserRoleChange(true);
    setUserRoles((prev) => {
      let newData = [...prev];
      const user = prev.filter((user) => user.upn === selectedUser)[0];
      if (user) {
        const userIndex = prev.indexOf(user);
        let currentRoles = [...user.roles];
        const index = currentRoles.indexOf(role);
        if (index !== -1) {
          currentRoles.splice(index, 1);
        } else {
          currentRoles = [...currentRoles, role];
        }
        newData[userIndex] = {
          upn: user.upn,
          roles: currentRoles,
        };
      } else {
        newData = [...newData, { upn: selectedUser, roles: [role] }];
      }

      return newData;
    });
  };
  const togglePermission = (permission: string) => {
    if (!selectedRole) return;
    setIsRolePermissionChange(true);
    let newData = [...rolePermissions];

    const role = rolePermissions.filter(
      (role) => role.role_name === selectedRole
    )[0];

    const roleIndex = rolePermissions.indexOf(role);
    let currentPermissions = { ...role.permission };
    currentPermissions[permission] = !currentPermissions[permission];
    newData[roleIndex] = {
      role_name: role.role_name,
      permission: currentPermissions,
    };
    let newRoleChangeData = [...roleChangeData];
    const roleChange = roleChangeData.filter(
      (role) => role.role_name === selectedRole
    )[0];
    const roleChangeIndex = roleChangeData.indexOf(roleChange);
    if (roleChangeIndex !== -1) {
      newRoleChangeData[roleChangeIndex] = {
        role_name: role.role_name,
        permission: currentPermissions,
      };
    } else {
      newRoleChangeData = [
        ...newRoleChangeData,
        {
          role_name: role.role_name,
          permission: currentPermissions,
        },
      ];
    }
    setRoleChangeData(newRoleChangeData);
    setRolePermissions(newData);
  };
  function handleOnSubmitUserRole() {
    dispatch(
      submitUserPermissionDataAsync({
        operation: "update_user_role",
        users: userRoles,
      })
    ).then((requestStatus) => {
      if (requestStatus?.meta.requestStatus == "fulfilled") {
        getData(true, false);
        setSelectedUser(null);
        setIsUserRoleChange(false);
      } else if (requestStatus?.meta.requestStatus == "rejected") {
        const error = JSON.parse(requestStatus.payload?.message);
        console.log(error);
        if (error.message === "InteractionRequiredAuthError") {
          msalInstance.acquireTokenRedirect({
            ...authScopes,
            account: msalInstance.getActiveAccount() as AccountInfo,
          });
        } else {
          if (error.status === 401) {
            navigate("/error", {
              state: { message: "userPermissionUnauthorized" },
            });
          }
          if (error.status === 500) {
            navigate("/error", {
              state: { message: "internalServerError" },
            });
          }
        }
      }
    });
  }
  function handleOnSubmitRolePermission() {
    dispatch(
      submitUserPermissionDataAsync({
        operation: "update_role_permission",
        roles: roleChangeData,
      })
    ).then((requestStatus) => {
      if (requestStatus?.meta.requestStatus == "fulfilled") {
        getData(false, true);
        setSelectedRole(null);
        setIsRolePermissionChange(false);
      } else if (requestStatus?.meta.requestStatus == "rejected") {
        const error = JSON.parse(requestStatus.payload?.message);
        console.log(error);
        if (error.message === "InteractionRequiredAuthError") {
          msalInstance.acquireTokenRedirect({
            ...authScopes,
            account: msalInstance.getActiveAccount() as AccountInfo,
          });
        } else {
          if (error.status === 401) {
            navigate("/error", {
              state: { message: "userPermissionUnauthorized" },
            });
          }
          if (error.status === 500) {
            navigate("/error", {
              state: { message: "internalServerError" },
            });
          }
        }
      }
    });
  }

  const renderUserGroup = (title: string, type: string) => (
    <>
      <Typography
        variant="subtitle2"
        sx={{ mt: 1, textAlign: "left", fontWeight: "bold" }}
      >
        {title}
      </Typography>
      <FormGroup>
        {type === "default" ? (
          <>
            {users
              .filter((u) => u.type === type)
              .map((user) => (
                <FormControlLabel
                  key={user.email}
                  control={
                    <Radio
                      className="secondaryColor"
                      checked={selectedUser === user.email}
                      onChange={() =>
                        setSelectedUser(
                          selectedUser === user.email ? null : user.email
                        )
                      }
                    />
                  }
                  label={user.email}
                />
              ))}
          </>
        ) : (
          <>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={
                selectedUser &&
                users
                  .filter((u) => u.type === type)
                  .some((u) => u.email === selectedUser)
                  ? selectedUser
                  : ""
              }
              onChange={(e) => setSelectedUser(e.target.value)}
              renderValue={(value) => value || `${t("select")} ${title}`}
              sx={{ mt: 1, textAlign: "left" }}
            >
              {users
                .filter((u) => u.type === type)
                .map((user) => (
                  <MenuItem key={user.email} value={user.email}>
                    {user.email}
                  </MenuItem>
                ))}
            </Select>
          </>
        )}
      </FormGroup>
    </>
  );
  return (
    <>
      {isLoading ? (
        <div className="spinner">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      ) : (
        <>
          <Stack
            spacing={3}
            sx={{ paddingTop: "36px" }}
            display={"inline-flex"}
            flexDirection={"column"}
            width={"100%"}
          >
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                className="secondaryColor"
                style={{
                  fontFamily: brand.titleFont,
                  fontSize: 24,
                  fontWeight: 400,
                  lineHeight: "36px",
                }}
              >
                {t("userRoleTitle")}
              </Typography>
            </Stack>
            <Box>
              <Grid container spacing={2}>
                {/* User Section */}
                <Grid item xs={12} md={6} sx={{ paddingTop: 0 }}>
                  <Card sx={{ boxShadow: "none", border: "none" }}>
                    <CardContent
                      sx={{ paddingTop: "4px", paddingLeft: "45px" }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("users")}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {renderUserGroup(t("defaultUsers"), "default")}
                      {renderUserGroup(t("users"), "user")}
                      {renderUserGroup(t("newUsers"), "new")}
                    </CardContent>
                  </Card>
                </Grid>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                {/* Role Section */}
                <Grid item xs={12} md={5}>
                  <Card sx={{ boxShadow: "none", border: "none" }}>
                    <CardContent sx={{ paddingTop: "4px" }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("roles")}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <FormGroup row>
                        {roles.map((role) => (
                          <FormControlLabel
                            key={role}
                            sx={{ width: "48%" }}
                            control={
                              <Checkbox
                                className="secondaryColor"
                                disabled={!selectedUser}
                                checked={
                                  !!selectedUser &&
                                  userRoles.filter(
                                    (user) => user.upn === selectedUser
                                  ).length > 0 &&
                                  userRoles
                                    .filter(
                                      (user) => user.upn === selectedUser
                                    )[0]
                                    ?.roles.indexOf(role) !== -1
                                }
                                onChange={() => toggleRole(role)}
                              />
                            }
                            label={role}
                          />
                        ))}
                      </FormGroup>
                      {isUserRoleChange && (
                        <>
                          {" "}
                          <Box mt={3} sx={{ textAlign: "right" }}>
                            <Button
                              variant="contained"
                              className="formButton"
                              onClick={() => discardUserRoleChanges()}
                              sx={{
                                width: "200px",
                                textTransform: "none",
                                fontWeight: "bold",
                              }}
                            >
                              {t("discardChangeButton")}
                            </Button>
                          </Box>
                          <Box mt={3} sx={{ textAlign: "right" }}>
                            <Button
                              variant="contained"
                              className="formButton"
                              onClick={() => handleOnSubmitUserRole()}
                              sx={{
                                width: "200px",
                                textTransform: "none",
                                fontWeight: "bold",
                              }}
                            >
                              {t("saveChangeButton")}
                            </Button>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Stack>

          <Divider sx={{ paddingTop: "152px" }} />
          <Stack
            spacing={3}
            sx={{ paddingTop: "25px" }}
            display={"inline-flex"}
            flexDirection={"column"}
            width={"100%"}
          >
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                className="secondaryColor"
                style={{
                  fontFamily: brand.titleFont,
                  fontSize: 24,
                  fontWeight: 400,
                  lineHeight: "36px",
                }}
              >
                {t("rolePermissionTitle")}
              </Typography>
            </Stack>
            <Box>
              <Grid container spacing={2}>
                {/* Roles Section */}
                <Grid
                  item
                  xs={12}
                  md={selectedRole ? 6 : 12}
                  sx={{ paddingTop: 0 }}
                >
                  <Card
                    sx={{
                      boxShadow: "none",
                      border: "none",
                    }}
                  >
                    <CardContent
                      sx={{ paddingTop: "4px", paddingLeft: "45px" }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("roles")}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <FormGroup>
                        {roles.map((role) => (
                          <FormControlLabel
                            key={role}
                            control={
                              <Checkbox
                                className="secondaryColor"
                                checked={selectedRole === role}
                                onChange={() =>
                                  setSelectedRole(
                                    selectedRole === role ? null : role
                                  )
                                }
                              />
                            }
                            label={role}
                          />
                        ))}
                      </FormGroup>
                    </CardContent>
                  </Card>
                </Grid>

                {selectedRole && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Grid item xs={12} md={5}>
                      <Card sx={{ border: "none", boxShadow: "none" }}>
                        <CardContent
                          sx={{ paddingTop: "4px", paddingLeft: "15px" }}
                        >
                          {Object.keys(userPermissionData.permissions).length > 0 && Object.keys(userPermissionData.permissions).map((permission, index) => (
                            <TableContainer
                              key={permission}
                              component={Paper}
                              sx={
                                index === 0
                                ? { borderTop: "1px solid rgba(224, 224, 224, 1)" }
                                : {}
                              }
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell key={permission}>
                                      <strong>{t(permission)}</strong>
                                    </TableCell>
                                    <TableCell></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {userPermissionData.permissions[permission].map((action) => (
                                    <TableRow key={action}>
                                      <TableCell sx={{ width: "90%" }}>
                                        {action}
                                      </TableCell>
                                      <TableCell>
                                        <IconButton
                                          onClick={() =>
                                            togglePermission(action)
                                          }
                                          size="small"
                                        >
                                          <CircleIcon
                                            style={
                                              !!selectedRole &&
                                              rolePermissions.filter(
                                                (role) =>
                                                  role.role_name ===
                                                  selectedRole
                                              )[0]?.permission[action] === true
                                                ? {
                                                    color: "#00C389",
                                                    fontSize: "14px",
                                                  }
                                                : {
                                                    color: "#D3D3D3",
                                                    fontSize: "14px",
                                                  }
                                            }
                                          />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ))}

                          {isRolePermissionChange && (
                            <>
                              <Box mt={3} sx={{ textAlign: "right" }}>
                                <Button
                                  variant="contained"
                                  className="formButton"
                                  onClick={() => discardRolePermissionChanges()}
                                  sx={{
                                    width: "200px",
                                    textTransform: "none",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {t("discardChangeButton")}
                                </Button>
                              </Box>

                              <Box mt={3} sx={{ textAlign: "right" }}>
                                <Button
                                  variant="contained"
                                  className="formButton"
                                  onClick={() => handleOnSubmitRolePermission()}
                                  sx={{
                                    width: "200px",
                                    textTransform: "none",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {t("saveChangeButton")}
                                </Button>
                              </Box>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Stack>
        </>
      )}
    </>
  );
};
